import hostConfigData from '../../../configs/hostConfig.json'

/**
 * API Host configuration map.
 * Each API host is paired with its corresponding cache host.
 *
 * Configuration loaded from configs/hostConfig.json - the single source of truth
 * shared across webpack, Next.js, and runtime code.
 */
export const API_HOST_CONFIG: Record<
  string,
  { cacheHost: string; env: string }
> = hostConfigData.apiHostConfig

/**
 * Default API hosts per environment (used when no API_HOST env is set).
 */
export const DEFAULT_API_HOSTS: Record<string, string> =
  hostConfigData.defaultApiHosts

/**
 * Fallback cache hosts for staging and production.
 */
export const FALLBACK_CACHE_HOSTS = hostConfigData.fallbackCacheHosts

/**
 * Get the cache host for a given API host.
 * Falls back to production cache host if API host is not in the config.
 *
 * @param apiHost - The API host URL
 * @returns The corresponding cache host URL
 */
export const getCacheHostForApiHost = (apiHost: string): string => {
  const config = API_HOST_CONFIG[apiHost]
  if (config) {
    return config.cacheHost
  }
  // Fallback: try to determine cache host based on URL pattern
  if (apiHost && (apiHost.includes('staging') || apiHost.includes('dev'))) {
    return FALLBACK_CACHE_HOSTS.staging
  }
  return FALLBACK_CACHE_HOSTS.production
}

/**
 * Check if an API host is a known production host.
 */
export const isProductionApiHost = (apiHost: string): boolean => {
  const config = API_HOST_CONFIG[apiHost]
  return config?.env === 'production'
}

/**
 * Check if an API host is a known staging/development host.
 */
export const isNonProductionApiHost = (apiHost: string): boolean => {
  const config = API_HOST_CONFIG[apiHost]
  if (config) {
    return config.env !== 'production'
  }
  // Fallback check for unknown hosts
  return apiHost.includes('staging') || apiHost.includes('dev')
}
